import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { seoService } from '../../services/seoService';
import { Search, TrendingUp, Target, Users, Filter, Download, RefreshCw } from 'lucide-react';
import KeywordResearchPanel from './components/KeywordResearchPanel';
import RankTrackingTable from './components/RankTrackingTable';
import ContentOptimizationPanel from './components/ContentOptimizationPanel';
import CompetitorAnalysisPanel from './components/CompetitorAnalysisPanel';

const KeywordResearchOptimizationCenter = () => {
  const navigate = useNavigate();
  const [keywords, setKeywords] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('research');
  const [filters, setFilters] = useState({
    category: 'all',
    difficulty: 'all',
    minVolume: 0,
    maxVolume: 10000
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [keywordsData, pagesData] = await Promise.all([
        seoService?.getAllKeywords(),
        seoService?.getAllPageOptimizations()
      ]);
      setKeywords(keywordsData);
      setPages(pagesData);
    } catch (err) {
      console.error('Error loading keyword data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleExport = () => {
    const csvContent = [
      ['Mot-clé', 'Volume', 'Difficulté', 'Position actuelle', 'Position cible', 'Catégorie'],
      ...keywords?.map(k => [
        k?.keyword,
        k?.search_volume,
        k?.difficulty,
        k?.current_ranking || '-',
        k?.target_ranking || '-',
        k?.category
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keywords-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  const filteredKeywords = keywords?.filter(k => {
    if (filters?.category !== 'all' && k?.category !== filters?.category) return false;
    if (filters?.difficulty !== 'all' && k?.difficulty !== filters?.difficulty) return false;
    if (k?.search_volume < filters?.minVolume || k?.search_volume > filters?.maxVolume) return false;
    return true;
  });

  const stats = {
    totalKeywords: keywords?.length || 0,
    avgVolume: keywords?.length > 0
      ? Math.round(keywords?.reduce((sum, k) => sum + k?.search_volume, 0) / keywords?.length)
      : 0,
    topRanked: keywords?.filter(k => k?.current_ranking && k?.current_ranking <= 10)?.length || 0,
    needsWork: keywords?.filter(k => !k?.current_ranking || k?.current_ranking > 20)?.length || 0
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600">Chargement du centre de recherche...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center max-w-md">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-red-800 font-medium mb-2">Erreur</p>
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Centre de recherche de mots-clés</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Recherche, suivi et optimisation SEO</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExport}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download size={18} />
              <span className="hidden sm:inline">Exporter</span>
            </button>
            <button
              onClick={handleRefresh}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <RefreshCw size={18} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg border bg-blue-50 text-blue-600 border-blue-200">
                <Search size={24} />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-2">Total mots-clés</h3>
            <p className="text-3xl font-bold text-gray-900">{stats?.totalKeywords}</p>
            <p className="text-xs text-gray-500 mt-1">Dans le portfolio</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg border bg-green-50 text-green-600 border-green-200">
                <TrendingUp size={24} />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-2">Volume moyen</h3>
            <p className="text-3xl font-bold text-gray-900">{stats?.avgVolume?.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">Recherches/mois</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg border bg-purple-50 text-purple-600 border-purple-200">
                <Target size={24} />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-2">Top 10 positions</h3>
            <p className="text-3xl font-bold text-gray-900">{stats?.topRanked}</p>
            <p className="text-xs text-gray-500 mt-1">Mots-clés bien positionnés</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg border bg-orange-50 text-orange-600 border-orange-200">
                <Users size={24} />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-2">À améliorer</h3>
            <p className="text-3xl font-bold text-gray-900">{stats?.needsWork}</p>
            <p className="text-xs text-gray-500 mt-1">Nécessite optimisation</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center mb-3">
            <Filter className="w-5 h-5 text-gray-600 mr-2" />
            <h3 className="text-sm font-semibold text-gray-900">Filtres</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Catégorie</label>
              <select
                value={filters?.category}
                onChange={(e) => setFilters({ ...filters, category: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500"
              >
                <option value="all">Toutes</option>
                <option value="general">Général</option>
                <option value="shipping">Envoi</option>
                <option value="logistics">Logistique</option>
                <option value="pricing">Tarifs</option>
                <option value="brand">Marque</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Difficulté</label>
              <select
                value={filters?.difficulty}
                onChange={(e) => setFilters({ ...filters, difficulty: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500"
              >
                <option value="all">Toutes</option>
                <option value="easy">Facile</option>
                <option value="medium">Moyen</option>
                <option value="hard">Difficile</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Volume min</label>
              <input
                type="number"
                value={filters?.minVolume}
                onChange={(e) => setFilters({ ...filters, minVolume: parseInt(e?.target?.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Volume max</label>
              <input
                type="number"
                value={filters?.maxVolume}
                onChange={(e) => setFilters({ ...filters, maxVolume: parseInt(e?.target?.value) || 10000 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('research')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'research' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-green-600'
              }`}
            >
              Recherche de mots-clés
            </button>
            <button
              onClick={() => setActiveTab('tracking')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'tracking' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-green-600'
              }`}
            >
              Suivi des positions
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'content' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-green-600'
              }`}
            >
              Optimisation contenu
            </button>
            <button
              onClick={() => setActiveTab('competitor')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'competitor' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-green-600'
              }`}
            >
              Analyse concurrentielle
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'research' && (
          <KeywordResearchPanel keywords={filteredKeywords} onRefresh={handleRefresh} />
        )}

        {activeTab === 'tracking' && (
          <RankTrackingTable keywords={filteredKeywords} onRefresh={handleRefresh} />
        )}

        {activeTab === 'content' && (
          <ContentOptimizationPanel keywords={filteredKeywords} pages={pages} onRefresh={handleRefresh} />
        )}

        {activeTab === 'competitor' && (
          <CompetitorAnalysisPanel keywords={filteredKeywords} />
        )}
      </div>
    </div>
  );
};

export default KeywordResearchOptimizationCenter;